import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Logo } from '../ui/Logo';
import { Button } from '../ui/Button';
export function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isPoliciesOpen, setIsPoliciesOpen] = useState(false);
  const location = useLocation();
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsPoliciesOpen(false);
  }, [location]);
  const navLinks = [{
    name: 'Home',
    path: '/'
  }, {
    name: 'Services',
    path: '/services'
  }, {
    name: 'About',
    path: '/about'
  }, {
    name: 'FAQs',
    path: '/faqs'
  }];
  const policyLinks = [{
    name: 'Privacy & Cookie Policy',
    path: '/privacy-cookie-policy'
  }, {
    name: 'Terms & Conditions',
    path: '/terms-conditions'
  }, {
    name: 'Cancellation Policy',
    path: '/cancellation-policy'
  }, {
    name: 'Refund Policy',
    path: '/refund-policy'
  }];
  const isPolicyPage = policyLinks.some(link => link.path === location.pathname);
  return <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white/90 backdrop-blur-md shadow-sm py-3' : 'bg-transparent py-5'}`} data-id="element-190">
      <div className="container mx-auto px-4 md:px-6" data-id="element-191">
        <div className="flex items-center justify-between" data-id="element-192">
          <Link to="/" className="flex-shrink-0" data-id="element-193">
            <Logo data-id="element-194" />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8" data-id="element-195">
            {navLinks.map(link => <Link key={link.path} to={link.path} className={`text-sm font-medium transition-colors hover:text-teal-600 ${location.pathname === link.path ? 'text-teal-600' : 'text-slate-600'}`} data-id="element-196">
                {link.name}
              </Link>)}

            {/* Policies Dropdown */}
            <div className="relative" data-id="element-197">
              <button onClick={() => setIsPoliciesOpen(!isPoliciesOpen)} className={`flex items-center gap-1 text-sm font-medium transition-colors hover:text-teal-600 ${isPolicyPage ? 'text-teal-600' : 'text-slate-600'}`} data-id="element-198">
                Policies
                <ChevronDown size={16} className={`transition-transform ${isPoliciesOpen ? 'rotate-180' : ''}`} data-id="element-199" />
              </button>

              <AnimatePresence data-id="element-200">
                {isPoliciesOpen && <motion.div initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: 10
              }} transition={{
                duration: 0.2
              }} className="absolute top-full right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-slate-100 py-2 overflow-hidden" data-id="element-201">
                    {policyLinks.map(link => <Link key={link.path} to={link.path} className={`block px-4 py-2 text-sm transition-colors hover:bg-slate-50 ${location.pathname === link.path ? 'text-teal-600 bg-teal-50' : 'text-slate-700'}`} data-id="element-202">
                        {link.name}
                      </Link>)}
                  </motion.div>}
              </AnimatePresence>
            </div>

            <Link to="/contact" data-id="element-203">
              <Button size="sm" variant="primary" data-id="element-204">
                Contact Us
              </Button>
            </Link>
          </nav>

          {/* Mobile Menu Button */}
          <button className="md:hidden p-2 text-slate-600 hover:text-slate-900" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} aria-label="Toggle menu" data-id="element-205">
            {isMobileMenuOpen ? <X size={24} data-id="element-206" /> : <Menu size={24} data-id="element-207" />}
          </button>
        </div>
      </div>

      {/* Mobile Navigation */}
      <AnimatePresence data-id="element-208">
        {isMobileMenuOpen && <motion.div initial={{
        opacity: 0,
        height: 0
      }} animate={{
        opacity: 1,
        height: 'auto'
      }} exit={{
        opacity: 0,
        height: 0
      }} className="md:hidden bg-white border-b border-slate-100 overflow-hidden" data-id="element-209">
            <div className="container mx-auto px-4 py-4 flex flex-col gap-4" data-id="element-210">
              {navLinks.map(link => <Link key={link.path} to={link.path} className={`text-base font-medium py-2 ${location.pathname === link.path ? 'text-teal-600' : 'text-slate-600'}`} data-id="element-211">
                  {link.name}
                </Link>)}

              {/* Mobile Policies Section */}
              <div className="border-t border-slate-100 pt-4" data-id="element-212">
                <button onClick={() => setIsPoliciesOpen(!isPoliciesOpen)} className={`flex items-center justify-between w-full text-base font-medium py-2 ${isPolicyPage ? 'text-teal-600' : 'text-slate-600'}`} data-id="element-213">
                  Policies
                  <ChevronDown size={20} className={`transition-transform ${isPoliciesOpen ? 'rotate-180' : ''}`} data-id="element-214" />
                </button>

                <AnimatePresence data-id="element-215">
                  {isPoliciesOpen && <motion.div initial={{
                opacity: 0,
                height: 0
              }} animate={{
                opacity: 1,
                height: 'auto'
              }} exit={{
                opacity: 0,
                height: 0
              }} className="pl-4 space-y-2 mt-2 overflow-hidden" data-id="element-216">
                      {policyLinks.map(link => <Link key={link.path} to={link.path} className={`block text-sm py-2 ${location.pathname === link.path ? 'text-teal-600' : 'text-slate-600'}`} data-id="element-217">
                          {link.name}
                        </Link>)}
                    </motion.div>}
                </AnimatePresence>
              </div>

              <Link to="/contact" className="w-full pt-2" data-id="element-218">
                <Button className="w-full" variant="primary" data-id="element-219">
                  Contact Us
                </Button>
              </Link>
            </div>
          </motion.div>}
      </AnimatePresence>
    </header>;
}